import { Component, ElementRef, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import swal from 'sweetalert2';
import { ConsumerInteractionModel, ConsumerInteractionProgressType, ConsumerInteractionType } from '../../../models/consumer/consumer-interaction.model';
import { LatestCreditStatusModel } from '../../../models/consumer/latest-credit-status.model';
import { ConsumerInteractionService } from '../../../services/consumer-interaction.service';
import { ConsumerService } from '../../../services/consumer.service';
import { DataService } from '../../../services/data.service';
import { getInteractionType } from '../../../utilities/consumer-utilities';
import { ConsumerDetailInjector } from '../consumer-detail-injector';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

enum CreditEditType {
    Reset = 'Reset',
    Adjustment = 'Adjustment',
    EmergencyCreditLimit = 'EmergencyCreditLimit'
}

@Component({
    selector: 'credit-status-item',
    templateUrl: './credit-status-item.component.html',
    styleUrls: ['./grid-item.scss']
})
export class CreditStatusItemComponent implements OnInit, OnDestroy {

    form: UntypedFormGroup;
    isSubmitted: boolean = false;
    isEditing: boolean = false;
    numberMask = createNumberMask({
        prefix: '',
        suffix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: ',',
        allowDecimal: true,
        decimalSymbol: '.',
        decimalLimit: 2,
        integerLimit: null,
        requireDecimal: false,
        allowNegative: true,
        allowLeadingZeroes: false
    });
    positiveNumberMask = createNumberMask({
        prefix: '',
        suffix: '',
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: ',',
        allowDecimal: true,
        decimalSymbol: '.',
        decimalLimit: 2,
        integerLimit: null,
        requireDecimal: false,
        allowNegative: false,
        allowLeadingZeroes: false
    });
    issues: any[] = [];
    subscriptions: any[] = [];
    latestCreditStatus: LatestCreditStatusModel;
    consumerId: string;
    clientId: number;
    expandVisible: boolean = true;
    hasData: boolean = false;
    hasLoading: boolean = true;
    showProgress: boolean = false;
    creditTimer: Subscription;
    progress: ConsumerInteractionProgressType = ConsumerInteractionProgressType.InProgress;
    interactionType: ConsumerInteractionType = ConsumerInteractionType.GetCreditStatus;
    consumerIntervalId: any;
    isProcessing: boolean = false;
    intervalList: any[] = [];

    constructor(
        private fb: UntypedFormBuilder,
        private consumerService: ConsumerService,
        private route: ActivatedRoute,
        private dataService: DataService,
        private consumerInteractionService: ConsumerInteractionService,
        consumerDetailInjector: ConsumerDetailInjector,
        private cdRef: ChangeDetectorRef,
        private elRef: ElementRef
    ) {
        this.issues = consumerDetailInjector.issues;

        this.form = fb.group({
            'type': [CreditEditType.Adjustment, Validators.required],
            'adjustmentAmount': [null, Validators.required],
            'emergencyCreditLimitAmount': [null, Validators.required],
        });

        this.subscriptions.push(
            this.form.controls.type.valueChanges.subscribe(
                value => this.onToggleType(value)
            )
        );
    }

    ngOnInit() {
        this.onToggleType(CreditEditType.Adjustment);

        this.clientId = this.route.parent.snapshot.params['clientId'];
        this.subscriptions.push(this.route
            .params
            .subscribe(params => {
                this.consumerId = params['consumerId'];
            }));

        this.consumerService.getLatestCreditStatus(this.clientId, this.consumerId).then((creditStatusRes) => {
            if (creditStatusRes) {
                this.dataService.creditStatusItemComponentResolved.next(true);
                this.latestCreditStatus = creditStatusRes;
            } else { this.dataService.creditStatusItemComponentResolved.next(false); }
        }).catch((reson) => {
            this.dataService.creditStatusItemComponentResolved.next(false);
        })

        this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
            let interaction = interactionRes as ConsumerInteractionModel[];
            this.progress = getInteractionType(interaction);
            this.isProcessing = false;
            if (this.progress == ConsumerInteractionProgressType.InProgress) {
                this.pollInteractionStatus();
            }
            this.showProgress = true;
        }).catch((error) => {
            this.showProgress = false;
            this.isProcessing = false;
            this.progress = ConsumerInteractionProgressType.Error;
        })
    }

    ngOnDestroy() {
        this.clearintervalList();
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    setExpandVisable(isVisible: boolean) {        
        this.expandVisible = isVisible;
    }

    onToggleType(type: CreditEditType) {
        if (type === CreditEditType.Adjustment) {
            this.form.controls.adjustmentAmount.enable();
            this.form.controls.emergencyCreditLimitAmount.disable();
        } else if (type == CreditEditType.EmergencyCreditLimit) {
            this.form.controls.emergencyCreditLimitAmount.enable();
            this.form.controls.adjustmentAmount.disable();
        } else if (type == CreditEditType.Reset) {
            this.form.controls.adjustmentAmount.disable();
            this.form.controls.emergencyCreditLimitAmount.disable();
        }

        this.cdRef.detectChanges();
    }

    get isPositiveAmount() {
        if (this.form.controls.type.value === CreditEditType.Adjustment) {
            let num = this.form.controls.adjustmentAmount.value;
            if (num == null || num == '') return false;
            return +num.replace(/,/g, "") > 0;
        }

        if (this.form.controls.type.value === CreditEditType.EmergencyCreditLimit) {
            let num = this.form.controls.emergencyCreditLimitAmount.value;
            if (num == null || num == '') return false;
            return +num.replace(/,/g, "") > 0;
        }
    }

    onEditClick(isSubmit: boolean = false) {
        let show = () => {
            reset();
            this.isEditing = true;
            this.elRef.nativeElement.parentElement.classList.add('module__edit');
        };

        let hide = () => {
            this.isEditing = false;
            this.elRef.nativeElement.parentElement.classList.remove('module__edit');
        };

        let reset = () => {
            this.isSubmitted = false;
            this.form.enable();
            this.form.reset();
            this.form.controls.type.setValue(CreditEditType.Adjustment);
            this.onToggleType(this.form.controls.type.value);
        }

        // toggle/cancel
        if (!isSubmit) {
            this.isEditing = !this.isEditing;
            this.isEditing ? show() : hide();
            return;
        }

        // save
        if (isSubmit) {
            this.isSubmitted = true;
            if (this.form.invalid || !this.form.dirty) return;

            swal({
                title: "Are you sure?",
                text: "You're about to make an adjustment to a consumer's credit status. Do you want to proceed?",
                confirmButtonText: 'Yes, please',
                confirmButtonColor: '#00539f',
                showCancelButton: true,
                cancelButtonText: "No, go back",
                cancelButtonColor: '#00539f',
            }).then(val => {
                if (val.value == true) {
                    this.form.disable();

                    let type = this.form.controls.type.value as CreditEditType;
                    if (type === CreditEditType.Adjustment || type == CreditEditType.Reset) {
                        let value = type == CreditEditType.Reset ? 0 : +this.form.controls.adjustmentAmount.value.replace(/,/g, '');

                        this.consumerService.setCreditStatus(this.clientId, this.consumerId, value).subscribe(
                            result => hide()
                        );

                    } else if (type === CreditEditType.EmergencyCreditLimit) {
                        let value = +this.form.controls.emergencyCreditLimitAmount.value;
                        this.consumerService.setEmergencyCreditLimit(this.clientId, this.consumerId, value).subscribe(
                            result => hide()
                        );
                    }
                }
            });
        }
    }

    onInfoClick() {
        swal({
            title: 'Credit status',
            html: '<p style="text-align: left;">This widget shows the latest credit status of the PAYG unit.</p>\
             <p style="text-align: left;">Expanding this widget you can see a line-chart history of the credit status.</p>',
            confirmButtonText: 'Close',
            confirmButtonColor: '#00539f'
        });
    }

    getInteraction() {
        this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
            let interaction = interactionRes as ConsumerInteractionModel[];
            this.progress = getInteractionType(interaction);
            this.isProcessing = false;
            this.showProgress = true;
            if (this.progress == ConsumerInteractionProgressType.Completed || this.progress == ConsumerInteractionProgressType.Error) {
                this.clearintervalList();
            }
        }).catch((error) => {
            this.showProgress = false;
            this.isProcessing = false;
            this.clearintervalList();
            this.progress = ConsumerInteractionProgressType.Error;
        });
    }

    clearintervalList() {

        for (let i = 0; i < this.intervalList.length; i++) {
            window.clearInterval(this.intervalList[i]);
        }

        for (let j = 0; j < 100; j++) {
            window.clearInterval(j);
        }

        this.intervalList = [];
    }

    pollInteractionStatus() {
        if (this.progress != ConsumerInteractionProgressType.InProgress) {
            this.isProcessing = true;
            this.consumerInteractionService.getDeviceCreditStatus(this.clientId, this.consumerId).then((creditStatusRes) => {
                this.getInteraction();

                this.intervalList.push(window.setInterval(() => {
                    this.getInteraction()
                }, 30000));
            }).catch((error) => {
                this.isProcessing = false;
                this.clearintervalList();
            })
        } else {
            this.intervalList.push(window.setInterval(() => {
                this.getInteraction()
            }, 30000));
        }
    }
}
